/** Learner Question Library types — mapped from student APIs + design attempt model. */

import type { GeneratedArtifact } from "../../../api/type"

export type DetailTab = "attempts" | "answer" | "solution" | "markscheme"

export type AttemptStatus =
  | "draft"
  | "submitted"
  | "marking"
  | "marked"
  | "flagged_for_review"
  | "failed"

export type AttemptConfidence = "high" | "medium" | "low"

export interface CriterionScore {
  label: string
  marksAwarded: number
  marksAvailable: number
  rationale: string
}

export interface Attempt {
  id: string
  attemptNumber: number
  answerText: string
  startedAt: string
  submittedAt: string | null
  timeSpentSeconds: number
  status: AttemptStatus
  score: number | null
  maxScore: number | null
  criteriaBreakdown: CriterionScore[] | null
  confidence: AttemptConfidence | null
  strengths?: string[]
  improvements?: string[]
  missingPoints?: string[]
  suggestedAnswer?: string
  examinerFeedback?: string
  assessmentError?: string | null
}

export interface QuestionListFilter {
  page_number: number
  per_page: number
  subject: string
  /** Multi-select hierarchy item ids (any depth). Preferred over level_1..4. */
  hierarchy_ids: string[]
  level_1: string
  level_2: string
  level_3: string
  level_4: string
  keyword: string
  year: string
  paper: string
  complexity: string
  sort_by: string
  sort_order: "asc" | "desc"
}

export const DEFAULT_QUESTION_LIST_FILTER: QuestionListFilter = {
  page_number: 1,
  per_page: 10,
  subject: "",
  hierarchy_ids: [],
  level_1: "",
  level_2: "",
  level_3: "",
  level_4: "",
  keyword: "",
  year: "",
  paper: "",
  complexity: "",
  sort_by: "newest",
  sort_order: "desc",
}

export interface StudentSubject {
  id: string
  title: string
  level_labels: string[]
}

export interface HierarchyItemDto {
  id: string
  subject_id: string
  level: number
  name: string
  parent_id: string | null
  parent_name: string | null
  child_count: number
}

export interface StudentQuestionListItem {
  id: string
  subject: string
  subject_title: string | null
  chapter: string | null
  topic: string | null
  chapter_id: string | null
  topic_id: string | null
  level_2_id: string | null
  level_2_name: string | null
  level_3_id: string | null
  level_3_name: string | null
  level_4_id: string | null
  level_4_name: string | null
  grade: string | null
  complexity: string | null
  source: string | null
  paper: string | null
  year: number | null
  marks: number | null
  question: string | null
  solution: string | null
  marking_scheme: string | null
  sub_parts?: Array<{
    label: string
    question?: string | null
    answer?: string | null
    solution?: string | null
    marks?: number | null
    marking_scheme?: string | null
  }>
  question_artifacts?: GeneratedArtifact[]
  solution_artifacts?: GeneratedArtifact[]
  isBookmarked: boolean
  created_at: string | null
  updated_at: string | null
  bookmarked_at?: string | null
}

export interface ResourceBlock {
  available: boolean
  content: string | null
  pdf_url: string | null
  artifacts?: GeneratedArtifact[]
}

export interface StudentQuestionDetail {
  id: string
  subject: string
  subject_title: string | null
  topic_hierarchy: {
    topic: string | null
    topic_id: string | null
    level_2_id: string | null
    level_2_name: string | null
    level_3_id: string | null
    level_3_name: string | null
    level_4_id: string | null
    level_4_name: string | null
    chapter: string | null
    chapter_id: string | null
  }
  year: number | null
  paper: string | null
  marks: number | null
  grade: string | null
  complexity: string | null
  source: string | null
  resources: {
    question: ResourceBlock
    solution: ResourceBlock
    marking_scheme: ResourceBlock
  }
  answer: string | null
  sub_parts: Array<{
    label: string
    question?: string | null
    answer?: string | null
    solution?: string | null
    marks?: number | null
    marking_scheme?: string | null
  }>
  student: {
    is_bookmarked: boolean
    notes: string | null
    tags: string[]
  }
}

export interface PaginationMeta {
  total: number
  page: number
  per_page: number
  total_pages: number
}

export interface QuestionCardViewModel {
  id: string
  subjectTitle: string
  yearLabel: string
  typeLabel: string
  difficulty: string
  snippet: string
  questionHtml: string
  topicPath: string
  isBookmarked: boolean
  hasSolution: boolean
  hasMarkingScheme: boolean
  marks: number | null
  partLabel: string | null
  questionArtifacts?: GeneratedArtifact[]
}
